import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Reveal, FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { SERVICES, ABOUT_IMAGE } from "@/data/site";

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About TecKube — Cloud, Software Delivery & Security Services"
      description="TecKube is a technology services business across cloud infrastructure, software delivery, application development and cybersecurity."
    />
    <PageHero
      eyebrow="About TecKube"
      title="Connecting what you build with what runs it."
      lead="TecKube is a technology services business working across cloud infrastructure, software delivery, application development and cybersecurity."
    >
      <FadeIn delay={0.4}>
        <p
          className="mt-8 font-display text-[13px] font-semibold uppercase tracking-[0.26em] text-primary"
          data-testid="about-tagline"
        >
          Modern Challenges, Innovative Solutions
        </p>
      </FadeIn>
    </PageHero>

    <section className="bg-paper pb-24 md:pb-32" data-testid="about-body">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-start gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <FadeIn delay={0.1}>
              <div className="group relative overflow-hidden rounded-3xl">
                <img
                  src={ABOUT_IMAGE}
                  alt="The TecKube team at work"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent" />
              </div>
            </FadeIn>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal>
              <p className="text-xl leading-relaxed text-ink md:text-2xl">
                We connect the work of building applications with the infrastructure and processes
                needed to run them.
              </p>
              <p className="mt-8 text-[17px] leading-relaxed text-soot md:text-lg">
                Our training activities through TecKube Academy also reflect our commitment to
                sharing technical knowledge.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-14 font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">
                What we work across
              </h2>
              <ul className="mt-8">
                {SERVICES.map((service, i) => (
                  <li key={service.slug} className="border-t border-slate-200">
                    <Link
                      to={`/services/${service.slug}`}
                      data-testid={`about-service-link-${service.slug}`}
                      className="group flex items-center justify-between py-5"
                    >
                      <span className="flex items-center gap-5">
                        <span className="font-mono text-xs text-primary">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display text-lg font-semibold tracking-tight text-ink transition-colors duration-300 group-hover:text-primary">
                          {service.title}
                        </span>
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-soot transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <CTABand />
  </div>
);

export default About;
